import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Pencil, Plus, RotateCcw, Save, Search, X } from "lucide-react";
import {
  CABINET_TYPE_DEFAULTS,
  ACCESSORIES,
  EXTRA_TYPES,
  WOOD_SPECIES_OPTIONS,
  ALL_CABINET_TYPES,
  type CabinetType,
} from "@/data/cabinetDefaults";
import {
  PRICING_CONSTANTS,
  PRICING_CONSTANTS_DEFAULTS,
} from "@/lib/pricingConstants";
import {
  upsertPricingSettings,
  deletePricingSettings,
  loadPricingSettings,
  settingKey,
  CABINET_RATE_DEFAULTS,
  ACCESSORY_PRICE_DEFAULTS,
  EXTRA_PRICE_DEFAULTS,
  SPECIES_MULTIPLIER_DEFAULTS,
} from "@/lib/pricingSettings";
import { loadAccessoryOverrides } from "@/lib/accessoriesCatalog";
import AccessoryEditorDialog, { type EditorTarget } from "./AccessoryEditorDialog";

type Row = {
  key: string;
  label: string;
  helper?: string;
  defaultValue: number;
  currentValue: number;
  prefix?: string;
  suffix?: string;
  step?: number;
  /** When set, this row represents an accessory the admin can fully edit. */
  accessoryId?: string;
  /** Displayed value = stored value × displayScale. Save divides input by displayScale. */
  displayScale?: number;
};

type Section = {
  title: string;
  description: string;
  rows: Row[];
  /** When set, the section header gets an "Add accessory" button scoped to this type. */
  addAccessoryFor?: CabinetType | "shared";
};


function buildSections(): Section[] {
  const pricedAccessories = ACCESSORIES.filter((a) => !a.isDynamicPanelPrice);

  const accessoryRules = (a: typeof ACCESSORIES[number]): string => {
    const rules: string[] = [];
    rules.push(`Available on: ${a.cabinetTypes.join(", ")}`);
    if (a.minWidth && a.maxWidth) rules.push(`Width ${a.minWidth}"–${a.maxWidth}"`);
    else if (a.minWidth) rules.push(`Min width ${a.minWidth}"`);
    else if (a.maxWidth) rules.push(`Max width ${a.maxWidth}"`);
    if (a.requiresStandardDepth) rules.push("Requires standard depth");
    if (a.requiresDoorDrawer) rules.push("Requires door + drawer front config");
    if (a.shallowDrawerInsert) rules.push("Lives in shallow (top) drawer");
    if (a.deepDrawerInsert) rules.push("Lives in deep (bottom) drawer");
    if (a.requiresSideSelection) rules.push("Requires left/right side selection");
    if (a.conflictGroup) rules.push(`Conflicts with others in group "${a.conflictGroup}" (only one allowed)`);
    if (a.isCustomRequest) rules.push("Custom request — contractor adds a description; final price confirmed by shop");
    if (a.isDynamicPanelPrice) rules.push("Price computed dynamically from cabinet dimensions");
    return [a.description, rules.join(" • ")].filter(Boolean).join(" — ");
  };

  // An accessory is "cabinet-type specific" if it applies to exactly one cabinet type.
  // Anything that spans multiple cabinet types goes into the shared Accessories section.
  const accessoryRow = (a: typeof ACCESSORIES[number]): Row => ({
    key: settingKey.accessory(a.id),
    label: a.label,
    helper: accessoryRules(a),
    defaultValue: ACCESSORY_PRICE_DEFAULTS[a.id] ?? a.price,
    currentValue: a.price,
    prefix: "$",
    accessoryId: a.id,
  });



  const cabinetSections: Section[] = ALL_CABINET_TYPES.map((type) => {
    const specific = pricedAccessories.filter(
      (a) => a.cabinetTypes.length === 1 && a.cabinetTypes[0] === type,
    );

    // Island Back uses two sub-type rates (Cabinet back vs Paneled back) instead
    // of a single base rate. Surface both rows so the admin can edit them here.
    const baseRows: Row[] =
      type === "Island Back"
        ? [
            {
              key: settingKey.constant("ISLAND_BACK_CABINET_PRICE_PER_LF"),
              label: "Cabinet back (doors) — base rate",
              helper: "Per linear foot — functional cabinet doors on the back of the island",
              defaultValue: PRICING_CONSTANTS_DEFAULTS.ISLAND_BACK_CABINET_PRICE_PER_LF,
              currentValue: PRICING_CONSTANTS.ISLAND_BACK_CABINET_PRICE_PER_LF,
              prefix: "$",
              suffix: "/ linear ft",
            },
            {
              key: settingKey.constant("PANEL_PRICE_PER_SQIN"),
              label: "Paneled back — inherits Finished Panel Rate",
              helper: "Paneled back pricing = height × width × Finished Panel Rate (edit under Pricing Constants)",
              defaultValue: PRICING_CONSTANTS_DEFAULTS.PANEL_PRICE_PER_SQIN,
              currentValue: PRICING_CONSTANTS.PANEL_PRICE_PER_SQIN,
              prefix: "$",
              suffix: "/ sq in",
              step: 0.001,
            },
          ]
        : type === "Panel"
        ? [
            {
              key: settingKey.constant("PANEL_PRICE_PER_SQIN"),
              label: "Panel — inherits Finished Panel Rate",
              helper: "Panel pricing = height × width × Finished Panel Rate (edit under Pricing Constants)",
              defaultValue: PRICING_CONSTANTS_DEFAULTS.PANEL_PRICE_PER_SQIN,
              currentValue: PRICING_CONSTANTS.PANEL_PRICE_PER_SQIN,
              prefix: "$",
              suffix: "/ sq in",
              step: 0.001,
            },
          ]
        : [
            {
              key: settingKey.cabinet(type),
              label: `${CABINET_TYPE_DEFAULTS[type].label} — base rate`,
              helper: type === "Fridge Cabinet"
                ? "Per inch of cabinet width (stored internally as $/linear ft × 12)"
                : "Per linear foot of cabinet width",
              defaultValue: CABINET_RATE_DEFAULTS[type],
              currentValue: CABINET_TYPE_DEFAULTS[type].pricePerLinearFt,
              prefix: "$",
              suffix: type === "Fridge Cabinet" ? "/ inch" : "/ linear ft",
              step: type === "Fridge Cabinet" ? 0.01 : 1,
              displayScale: type === "Fridge Cabinet" ? 1 / 12 : undefined,
            },
          ];

    const rows: Row[] = [...baseRows, ...specific.map(accessoryRow)];
    return {
      title: CABINET_TYPE_DEFAULTS[type].label,
      description:
        specific.length > 0
          ? `Base rate and ${specific.length} accessory${specific.length === 1 ? "" : "ies"} unique to ${CABINET_TYPE_DEFAULTS[type].label} cabinets.`
          : `Base rate for ${CABINET_TYPE_DEFAULTS[type].label} cabinets.`,
      rows,
      addAccessoryFor: type,
    };
  });

  const sharedAccessories = pricedAccessories.filter((a) => a.cabinetTypes.length > 1);

  return [
    ...cabinetSections,
    {
      title: "Shared Accessories",
      description: "Accessories available across multiple cabinet types.",
      rows: sharedAccessories.map(accessoryRow),
      addAccessoryFor: "shared",
    },

    {
      title: "Pricing Constants",
      description: "Global numbers used by the pricing formulas.",
      rows: [
        {
          key: settingKey.constant("DRAWER_SURCHARGE"),
          label: "Drawer surcharge",
          helper: "Added per extra drawer above shop-standard 1-drawer config",
          defaultValue: PRICING_CONSTANTS_DEFAULTS.DRAWER_SURCHARGE,
          currentValue: PRICING_CONSTANTS.DRAWER_SURCHARGE,
          prefix: "$",
          suffix: "/ drawer",
        },
        {
          key: settingKey.constant("PANEL_PRICE_PER_SQIN"),
          label: "Finished panel rate",
          helper: "Per square inch (height × depth × rate), applied to finished end panels",
          defaultValue: PRICING_CONSTANTS_DEFAULTS.PANEL_PRICE_PER_SQIN,
          currentValue: PRICING_CONSTANTS.PANEL_PRICE_PER_SQIN,
          prefix: "$",
          suffix: "/ sq in",
          step: 0.001,
        },
        {
          key: settingKey.constant("DECORATIVE_PANEL_ADDON"),
          label: "Decorative panel add-on",
          helper: "Added on top of base panel price for decorative panels",
          defaultValue: PRICING_CONSTANTS_DEFAULTS.DECORATIVE_PANEL_ADDON,
          currentValue: PRICING_CONSTANTS.DECORATIVE_PANEL_ADDON,
          prefix: "$",
        },
        {
          key: settingKey.constant("CUSTOM_ITEM_PRICE_PER_SQFT"),
          label: "Custom item rate",
          helper:
            "Used by the Custom Item size estimator — developed box surface area (2·HD + 2·WD + 2·WH) ÷ 144 × rate",
          defaultValue: PRICING_CONSTANTS_DEFAULTS.CUSTOM_ITEM_PRICE_PER_SQFT,
          currentValue: PRICING_CONSTANTS.CUSTOM_ITEM_PRICE_PER_SQFT,
          prefix: "$",
          suffix: "/ sq ft",
          step: 0.5,
        },
      ],
    },
    {
      title: "Extras Prices",
      description: "Mouldings, hoods, shelves, posts — billed by unit (linear ft, sq ft, count).",
      rows: EXTRA_TYPES.map((e) => ({
        key: settingKey.extra(e.id),
        label: e.label,
        helper: `Per ${e.unit}`,
        defaultValue: EXTRA_PRICE_DEFAULTS[e.id],
        currentValue: e.pricePerUnit,
        prefix: "$",
      })),
    },
    {
      title: "Wood Species Multipliers",
      description: "Base price is multiplied by this value when the species is selected. 1.0 = no upcharge.",
      rows: WOOD_SPECIES_OPTIONS.map((s) => ({
        key: settingKey.species(s.name),
        label: s.name,
        defaultValue: SPECIES_MULTIPLIER_DEFAULTS[s.name],
        currentValue: s.priceMultiplier,
        suffix: "×",
        step: 0.05,
      })),
    },
  ];
}

const AdminPricingTab = () => {
  const [sections, setSections] = useState<Section[]>([]);
  // values keyed by setting key, as strings (to allow free editing)
  const [values, setValues] = useState<Record<string, string>>({});
  const [savingSection, setSavingSection] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorTarget, setEditorTarget] = useState<EditorTarget | null>(null);
  const [search, setSearch] = useState("");

  const refresh = async () => {
    setLoading(true);
    await Promise.all([loadPricingSettings(true), loadAccessoryOverrides(true)]);
    const built = buildSections();
    setSections(built);
    const init: Record<string, string> = {};
    for (const sec of built) {
      for (const row of sec.rows) {
        const shown = row.displayScale ? row.currentValue * row.displayScale : row.currentValue;
        init[row.key] = String(shown);
      }
    }
    setValues(init);
    setLoading(false);
  };

  useEffect(() => {
    refresh();
  }, []);

  const openCreate = (presetCabinetTypes?: CabinetType[]) => {
    setEditorTarget({ mode: "create", presetCabinetTypes });
    setEditorOpen(true);
  };
  const openEdit = (id: string) => {
    setEditorTarget({ mode: "edit", id });
    setEditorOpen(true);
  };


  const overrides = useMemo(() => {
    const set = new Set<string>();
    for (const sec of sections) {
      for (const row of sec.rows) {
        if (row.currentValue !== row.defaultValue) set.add(row.key);
      }
    }
    return set;
  }, [sections]);

  const handleChange = (key: string, v: string) => {
    setValues((prev) => ({ ...prev, [key]: v }));
  };

  const handleResetRow = (row: Row) => {
    const shown = row.displayScale ? row.defaultValue * row.displayScale : row.defaultValue;
    setValues((prev) => ({ ...prev, [row.key]: String(shown) }));
  };

  const handleSaveSection = async (sec: Section) => {
    setSavingSection(sec.title);
    try {
      const toUpsert: Array<{ key: string; value: number }> = [];
      const toDelete: string[] = [];
      for (const row of sec.rows) {
        const raw = values[row.key];
        const shown = Number(raw);
        if (!Number.isFinite(shown)) {
          toast.error(`Invalid number for "${row.label}"`);
          setSavingSection(null);
          return;
        }
        const num = row.displayScale ? shown / row.displayScale : shown;
        // If user reset to default, remove the override row.
        if (num === row.defaultValue) {
          if (overrides.has(row.key)) toDelete.push(row.key);
        } else if (num !== row.currentValue) {
          toUpsert.push({ key: row.key, value: num });
        }
      }

      if (toUpsert.length === 0 && toDelete.length === 0) {
        toast.info("No changes to save");
        setSavingSection(null);
        return;
      }

      await upsertPricingSettings(toUpsert);
      await deletePricingSettings(toDelete);
      toast.success(`${sec.title} saved`);
      await refresh();
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message ?? "Failed to save");
    } finally {
      setSavingSection(null);
    }
  };

  if (loading) {
    return <p className="text-muted-foreground text-sm">Loading pricing settings...</p>;
  }

  const q = search.trim().toLowerCase();
  const filteredSections = q
    ? sections
        .map((sec) => ({
          ...sec,
          rows: sec.rows.filter(
            (r) =>
              r.label.toLowerCase().includes(q) ||
              (r.helper?.toLowerCase().includes(q) ?? false),
          ),
        }))
        .filter((sec) => sec.rows.length > 0)
    : sections;

  return (
    <div className="space-y-8">
      <div className="border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
        Changes apply to all <strong className="text-foreground">future</strong> quote calculations across the contractor portal,
        admin estimator, and public quote flow. Existing saved project totals are not recalculated.
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <Input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search accessories, base rates, constants..."
          className="pl-9 pr-9"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
            aria-label="Clear search"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {filteredSections.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-8">
          No matches for "{search}".
        </p>
      )}

      {filteredSections.map((sec) => (
        <section key={sec.title} className="border border-border">
          <header className="border-b border-border px-4 sm:px-6 py-4 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-medium text-foreground" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                {sec.title}
              </h2>
              <p className="text-xs text-muted-foreground mt-1 max-w-2xl">{sec.description}</p>
            </div>
            <div className="flex items-center gap-2">
              {sec.addAccessoryFor && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    openCreate(
                      sec.addAccessoryFor === "shared"
                        ? undefined
                        : [sec.addAccessoryFor as CabinetType],
                    )
                  }
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add accessory
                </Button>
              )}
              <Button
                onClick={() => handleSaveSection(sec)}
                disabled={savingSection === sec.title}
                size="sm"
              >
                <Save className="w-4 h-4 mr-2" />
                {savingSection === sec.title ? "Saving..." : "Save section"}
              </Button>
            </div>
          </header>

          <div className="divide-y divide-border">
            {sec.rows.map((row) => {
              const isOverridden = overrides.has(row.key);
              return (
                <div
                  key={row.key}
                  className="px-4 sm:px-6 py-3 flex flex-wrap items-center gap-3 sm:gap-4"
                >
                  <div className="flex-1 min-w-[180px]">
                    <Label className="text-sm text-foreground font-medium flex items-center gap-2">
                      {row.label}
                      {isOverridden && (
                        <span className="text-[10px] uppercase tracking-wider text-primary border border-primary/40 px-1.5 py-0.5">
                          custom
                        </span>
                      )}
                    </Label>
                    {row.helper && (
                      <p className="text-xs text-muted-foreground mt-0.5">{row.helper}</p>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {row.prefix && (
                      <span className="text-sm text-muted-foreground">{row.prefix}</span>
                    )}
                    <Input
                      type="number"
                      step={row.step ?? 1}
                      value={values[row.key] ?? ""}
                      onChange={(e) => handleChange(row.key, e.target.value)}
                      className="w-28 text-right"
                    />
                    {row.suffix && (
                      <span className="text-xs text-muted-foreground whitespace-nowrap">{row.suffix}</span>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleResetRow(row)}
                      disabled={Number(values[row.key]) === (row.displayScale ? row.defaultValue * row.displayScale : row.defaultValue)}
                      title={`Reset to ${row.displayScale ? row.defaultValue * row.displayScale : row.defaultValue}`}
                      className="h-8 px-2"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                    </Button>
                    {row.accessoryId && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => openEdit(row.accessoryId!)}
                        title="Edit accessory rules"
                        className="h-8 px-2"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}

      <AccessoryEditorDialog
        open={editorOpen}
        target={editorTarget}
        onOpenChange={setEditorOpen}
        onSaved={refresh}
      />
    </div>
  );
};

export default AdminPricingTab;
